import { Config } from 'config';

import { forEach, map, assign, isString } from 'lodash';

import { Matrix, Point, Bounds, Document, DocumentTag, Color, Font,
         Figure, Figures, Model3D, ModelData3D, TextureMap3D, QuantumImage } from 'cdl';

import { toCoreCase, objectToCoreCase, objectFromCore, propertiesToCore } from './core-util';
import { loadFontDataFromCore$ } from './core-font-data';

let { Cx } = window;

DocumentTag.fromCore = function( rm ) {
  return objectFromCore(DocumentTag,rm);
};

Document.fromCore = function( model, bcFixes ) {
  const doc = Document.fromXmlString(model.Cdl,bcFixes);

  doc.name =     model.Name;
  doc.class1 =   model.Class1;
  doc.class2 =   model.Class2;
  doc.class3 =   model.Class3;
  doc.modified = model.Modified;
  doc.notes =    model.Notes;
  doc.owned =    model.Owned;
  doc.docType =  model.DocType; // We should check here, docType: Doc3D and xType: System.Document3D should be consistent

  if( doc._owned ) {
    doc.id = model.Id; // sets the ID, which means by default the user saves over the top
    doc._isSaved = true;
    doc.externalId = model.ExternalId;
  }
  // Tags
  doc.tags = map( model.Tags, DocumentTag.fromCore );

  doc.dropPreviousHistoryOnNextCommit();
  return doc ;
};

Document.fromCore$ = function( model ) {
  return Document.backwardCompatibleDecoding$(Document.fromCore,model);
};

// Point
//-----------------------------------------------------------------------------

Point.prototype.toCore = function() {
  return { X: this.x, Y: this.y };
};
Point.fromCore = function(c) {
  return new Point(c.X,c.Y);
};

// Matrix
//-----------------------------------------------------------------------------

const _toFixed = function( x ) {
  return (+x.toFixed(4));
};

Matrix.prototype.toCore = function() {
  const _this = this;
  return { S00: _toFixed(_this.s00), S01: _toFixed(_this.s01),
           S10: _toFixed(_this.s10), S11: _toFixed(_this.s11),
           DX : _toFixed(_this.dx ), DY : _toFixed(_this.dy ) };
};

Matrix.fromCore = function( m ) {
  return new Matrix({ s00: m.S00, s01: m.S01 ,
                      s10: m.S10, s11: m.S11 ,
                      dx : m.DX , dy : m.DY  });
};

// Bounds
//-----------------------------------------------------------------------------

Bounds.fromCore = function( m ) {
  return new Bounds(m.LX,m.LY,m.HX,m.HY);
};

// Color
//-----------------------------------------------------------------------------

Color.prototype.toCore = function() {
  return propertiesToCore(this);
};
Color.fromCore = function( rm ) {
  return objectFromCore(Color,rm);
};

// Font
//-----------------------------------------------------------------------------

assign( Font.prototype,{

  coreSrc() {
    // [TODO] Core.Font.Url.MatchData is wrong
    return Cx.Core.getUrl('Core2/Font/MatchData', this.toCore() );
  },
  toCore() {
    const _this = this, co = {};
    forEach( _this.Properties, (c,p) => {
      const v = _this[p];
      if( v !== null ) {
        co[ p === 'id' ? 'Id' : ( c.tag || toCoreCase(p) ) ] = v ;
      }
    });
    return co;
  }
});

Font.fromCore = function( rm ) {
  const font = objectFromCore(Font,rm);
  const id = rm.Id;
  if( id ) {
    font._id = id;
  }
  return font;
};

export const GlobalCoreResourceManager = Cx.GlobalCoreResourceManager = function( config ) {
  assign(this,config);
};

GlobalCoreResourceManager.prototype = {

  get id() {
    return Cx.Core.getConfig();
  },

  // Font

  loadFontData$( font, chars, sizeRef ) {
    return loadFontDataFromCore$(font,chars,sizeRef);
  },

  fontThumbnailUrl( font, config ) {
    const coreConfig = objectToCoreCase(config);
    return Cx.Core.Font.thumbnailUrl(assign({ Font: font.toCore() }, coreConfig));
  },

  // Image

  // { id, preview } => { width, height, preview:base64 }
  imageInfo$( m ) {
    return Cx.Core.Image.readInfo$({
      Id: m.id, Preview: m.preview,
      Compress: Config.compressQuantums
    }) .then( r => {
    const preview = r.Preview;
    return { width: r.Width, height: r.Height,
             preview: preview && ('data:image/png;base64,'+preview) };
    });
  },

  // { id, size } => base64
  renderImage$( m ) {
    return Cx.Core.Image.readData$({ AsBase64: true,
      Id: m.id, Quantum: m.size || null,
      Compress: Config.compressQuantums
    }) .then( r => 'data:image/png;base64,'+r.Data );
  },

  // { id, size } => url
  imageUrl( m ) {
    return Cx.Core.Image.readDataUrl({
      Id: m.id, Quantum: m.size || null,
      Compress: Config.compressQuantums
    });
  },


  // Document

  loadDocument$( model, options ) {
    if( isString(model) ) {
      model = { Id: model };
    }
    else {
      model = objectToCoreCase(model);
    }
    return Cx.Core.Document.read$(model,options) .then(Document.fromCore$);
  },
  saveDocument$( doc ) {

    const requestModel = {
      Cdl       : doc.toXmlString(),
      Name      : doc._name,
      Class1    : doc._class1,
      Class2    : doc._class2,
      Class3    : doc._class3,
      Modified  : doc._modified,
      Notes     : doc._notes,
      DocType   : doc._docType,
      ExternalId: doc._externalId
    };

    requestModel.Tags = [];
    if( doc._category ) {
      requestModel.Tags.push( { Name: doc._category } );
    }

    // If we have an id because we owned this document, overwrite the old one
    if( doc._id ) {
      requestModel.Id = doc._id;
    }

    let operation = requestModel.Id ? 'update$' : 'create$';

    return Cx.Core.Document[operation](requestModel) .then( responseModel => {

    doc.archived = true;
    doc.owned = true;
    if(responseModel.Id) {
      doc.id = responseModel.Id;
    }
    });
  },

  // 3D Resources

  loadDocument3D$( config, options ) {
    return this.loadDocument$(config,options);
  },
  saveDocument3D$( doc ) {
    return this.saveDocument$(doc);
  },

  loadModel3D$( id ) {
    return Cx.Core.Model3D.read$({ Id: id }) .then( model => {
    const model3D = Model3D.fromXmlString(model.Data);
    model3D.id = id;
    return model3D;
    });
  },
  saveModel3D$( model3D ) {
    let operation = model3D._id ? 'update$' : 'create$';

    return Cx.Core.Model3D[operation]({ Data: model3D.toXmlString() })
    .then( responseModel => {

    if(responseModel.Id) {
      model3D.id = responseModel.Id;
    }
    return model3D;
    });
  },

  loadModelData3D$( id ) {
    return Cx.Core.ModelData3D.read$({Id:id}) .then( model => {
    const modelData3D = new ModelData3D({data:JSON.parse(model.Data)});
    modelData3D.id = id;
    return modelData3D;
    });
  },
  saveModelData3D$( modelData3D ) {
    let jsonString = JSON.stringify(modelData3D.data);
    let operation = modelData3D.id ? 'update$' : 'create$';
    return Cx.Core.ModelData3D[operation]({ Data: jsonString }) .then( responseModel => {
    if( responseModel.Id ) {
      modelData3D.id = responseModel.Id;
    }
    return modelData3D;
    });
  },

  createTextureMap3D$( file ) {
    return Cx.Core.Texture3D.create$(file) .then( response => {
    return new TextureMap3D({ id: response.Id });
    });
  },

  renderTexture3D$( model ) {
    return Cx.Core.Texture3D.render$({ Id: model.id, Size: model.size }) .then( model => {
    return model.Data;
    });
  },

  // Figure Operations

  optimizeFigures$( figures ) {
    return Cx.Core.Document.optimize$({ Cdl: Figures.toXmlString(figures) }) .then( optimizedDocModel => {
    return Figures.fromXmlString(optimizedDocModel.Cdl);
    });
  }
};

// Hack Cx.Core.fileUpload$, this is going to be a formal part
// of Grafika at one point in the future

if( QuantumImage.downScaleQuantumsLocally() ) {

// We need to discuss with Justin about this. To create togehter a good abstraction
// for our apps

// I am overloading Cx.Core.fileUpload$ to be able to grab images directly without
// calling to ReadInfo/ReadData
// We could also explore importing the file without waiting for the file to upload
// But this will require a little bit more work
const core_fileUpload$ = Cx.Core.fileUpload$;

Cx.Core.fileUpload$ = function( url, file, requestModel, progressHandler, options ) {
  return core_fileUpload$( url, file, requestModel, progressHandler, options ) .then( response => {

    if( url.indexOf('Texture3D/Create') !== -1 ) {
      return response;
    }

    const { type } = file;
    if( type === 'image/jpeg' || type === 'image/png' ) {

      let imageLink;

      // If the user issue a Doc/Import with the image
      const cdl = response.Cdl;
      if( cdl ) {
        imageLink = Figure.fromXmlString(cdl).imageLink;
      }

      // If the user issue a Image/Create
      if( ! imageLink && url.indexOf('Image/Create') !== -1 ) {
        imageLink = response.Id;
      }

      if( imageLink ) {

        // Cache the quantums locally
        QuantumImage.cacheFromFile$(imageLink,file);

        // Ask the server to generate the quantum images so they are ready next time
        Cx.Core.Image.generateQuantums$({ Id: imageLink });
      }
    }
    return response;
  });
};

}



/** WEBPACK FOOTER **
 ** ./src/resources/core.js
 **/